import { Group, Stack } from "@mantine/core"
import {
  getFilteredRowModel,
  getPaginationRowModel,
  OnChangeFn,
  PaginationState,
} from "@tanstack/react-table"
import { ColumnManager, ExportType, ExportButton, FilterOptions } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { SortBuilder, SortBuilderProps } from "~/components/SortBuilder"
import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { FilterBuilderWrapper } from "~/components/table/FilterBuilderWrapper"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindProductionSitesForBrandsRequestFiltersDto,
  FindProductionSitesForBrandsRequestSortsDto,
  FindProductionSitesForBrandsResponseDto,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useColumns } from "~/pages/Brand/Suppliers/List/SupplierProductionSites/columns.tsx"
import { useDownloadSuppliersProdSitesCSVMutation } from "~/pages/Brand/Suppliers/hooks"

interface Props {
  productionSitesData: FindProductionSitesForBrandsResponseDto
  setFiltersValue: Dispatch<
    SetStateAction<FindProductionSitesForBrandsRequestFiltersDto>
  >
  setPagination: OnChangeFn<PaginationState>
  pagination: PaginationState
  filtersValue: FindProductionSitesForBrandsRequestFiltersDto
  searchProductionSites: string
  setSearchProductionSites: (value: string) => void
  debouncedProductionSites: string
  isFetching: boolean
  setProductionSiteSorts: Dispatch<
    SetStateAction<FindProductionSitesForBrandsRequestSortsDto>
  >
  productionSiteSorts: FindProductionSitesForBrandsRequestSortsDto
  filterOptions: FilterOptions<FindProductionSitesForBrandsRequestFiltersDto>[]
  sortOptions: SortBuilderProps<FindProductionSitesForBrandsRequestSortsDto>["options"]
}

export const SupplierProductionSites = ({
  productionSitesData,
  setPagination,
  pagination,
  searchProductionSites,
  setSearchProductionSites,
  debouncedProductionSites,
  isFetching,
  setProductionSiteSorts,
  productionSiteSorts,
  filterOptions,
  sortOptions,
  filtersValue,
  setFiltersValue,
}: Props) => {
  const { t } = useTranslation(["suppliers", "common"])
  const { count, productionSites, prodSiteFieldHeaders } = productionSitesData
  const columns = useColumns(prodSiteFieldHeaders)
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const { mutate: downloadMutation, isPending: isSuppliersCSVDownloading } =
    useDownloadSuppliersProdSitesCSVMutation({
      searchProductionSite: debouncedProductionSites,
    })

  const table = useCustomReactTable({
    data: productionSites,
    columns,
    initialColumnPinning: { left: ["name"] },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    state: {
      globalFilter: searchProductionSites,
      pagination,
    },
    rowCount: count,
    onGlobalFilterChange: setSearchProductionSites,
    manualPagination: true,
    onPaginationChange: setPagination,

    getFilteredRowModel: getFilteredRowModel(),

    getPaginationRowModel: getPaginationRowModel(),
  })

  const columnManagerTranslations = useGetColumnManagerTranslations()
  return (
    <Stack>
      <Views
        tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_PRODUCTION_SITES}
      />
      <Group justify="space-between">
        <Group>
          <SearchBar
            filtersValue={filtersValue}
            setFiltersValue={setFiltersValue}
            placeholder={t("suppliers:productionSites.search.placeholder")}
            isFetching={isFetching}
          />
          <SortBuilder
            sorts={productionSiteSorts}
            setSorts={setProductionSiteSorts}
            options={sortOptions}
          />
          <FilterBuilderWrapper
            title={t("common:filter")}
            searchPlaceholder={t(
              "suppliers:productionSites.filters.searchPlaceholder"
            )}
            options={filterOptions}
            setFilters={setFiltersValue}
            filters={filtersValue}
          />
          <ColumnManager
            table={table}
            translations={columnManagerTranslations}
            isOpen={isColumnManagerOpen}
            setIsOpen={setIsColumnManagerOpen}
          />
          <ViewHeader
            tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_PRODUCTION_SITES}
          />
        </Group>
        <ExportButton
          label={t("common:actionButtons.export")}
          exportOptions={[
            {
              type: ExportType.CSV,
              label: t("common:actionButtons.exportAsCsv"),
              exportFunction: downloadMutation,
              isLoading: isSuppliersCSVDownloading,
            },
          ]}
        />
      </Group>
      <CustomTable
        table={table}
        pagination={true}
        manualPaginationCount={count}
        stickyHeader
        setOpenColumnManager={setIsColumnManagerOpen}
        isFetching={isFetching}
      />
    </Stack>
  )
}
